import { useEffect, useRef } from 'react';
import {
  createChart,
  CandlestickSeries,
  HistogramSeries,
  LineSeries,
} from 'lightweight-charts';
import { computeEMA, computeRSI, getLWCTheme } from '../utils/chartUtils';

/**
 * CombinedChart — single Lightweight Charts instance with four stacked panes:
 *   Pane 0 (main, ~50%): Candlestick OHLC + max pain dashed line
 *   Pane 1 (~13%):       Volume histogram (stocks only; omitted for indices)
 *   Pane 2 (~18%):       EMA 9 (blue) + EMA 30 (amber)
 *   Pane 3 (~18%):       RSI 14 (violet) with 70/30 reference lines
 *
 * Max pain bubbles are drawn on a canvas overlay (not LWC markers) so that
 * radius is pixel-precise and proportional to total_pain via log scaling.
 *
 * Props:
 *   data    {Array}   — [{date, open, high, low, close, volume, iv, max_pain, total_pain}]
 *   isIndex {boolean} — true → skip volume pane
 *   isDark  {boolean}
 */
const fmt = (n) =>
  n != null ? Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '—';

const fmtCr = (n) => {
  if (n == null) return null;
  const cr = n / 10_000_000;
  return `${cr.toLocaleString('en-IN', { maximumFractionDigits: 1 })} Cr`;
};

export default function CombinedChart({ data, isIndex, isDark, showMaxPain = true, showSR = false, pivots = null }) {
  const containerRef = useRef(null);
  const chartRef     = useRef(null);
  const tooltipRef   = useRef(null);
  const canvasRef    = useRef(null);

  // Stable refs so handlers never go stale
  const dataRef        = useRef(data);
  const mpDataRef      = useRef([]);
  const showMaxPainRef = useRef(showMaxPain);
  const showSRRef      = useRef(showSR);
  const pivotsRef      = useRef(pivots);

  // Series refs
  const candleRef  = useRef(null);
  const volRef     = useRef(null);
  const mpLineRef  = useRef(null);
  const ema9Ref    = useRef(null);
  const ema30Ref   = useRef(null);
  const rsiRef     = useRef(null);

  // S/R price line refs (so we can remove them when toggling)
  const srLinesRef = useRef([]);

  // Pane refs (for resize)
  const volPaneRef = useRef(null);
  const emaPaneRef = useRef(null);
  const rsiPaneRef = useRef(null);

  // ── Compute pane heights from total container height ──────────────────
  const paneHeights = (totalH) => {
    const vol = isIndex ? 0 : Math.round(totalH * 0.13);
    const ema = Math.round(totalH * 0.18);
    const rsi = Math.round(totalH * 0.18);
    return { vol, ema, rsi };
  };

  // ── Add / remove S/R price lines based on current showSR + pivots refs ──
  function applySRLines() {
    const candle = candleRef.current;
    // Always clear existing lines first
    srLinesRef.current.forEach((line) => {
      try { candle?.removePriceLine(line); } catch (_) {}
    });
    srLinesRef.current = [];

    if (!showSRRef.current || !pivotsRef.current || !candle) return;

    const p = pivotsRef.current;
    const defs = [
      { price: p.r3, color: 'rgba(239,68,68,0.85)', title: 'R3' },
      { price: p.r2, color: 'rgba(239,68,68,0.60)', title: 'R2' },
      { price: p.r1, color: 'rgba(239,68,68,0.35)', title: 'R1' },
      { price: p.s1, color: 'rgba(34,197,94,0.35)',  title: 'S1' },
      { price: p.s2, color: 'rgba(34,197,94,0.60)',  title: 'S2' },
      { price: p.s3, color: 'rgba(34,197,94,0.85)',  title: 'S3' },
    ];
    srLinesRef.current = defs.map(({ price, color, title }) =>
      candle.createPriceLine({ price, color, lineWidth: 1, lineStyle: 1, axisLabelVisible: true, title })
    );
  }

  // ── Draw bubbles + pane title labels onto the canvas overlay ────────────
  // Reads refs/props directly so it can be called from anywhere.
  function drawOverlay() {
    const canvas = canvasRef.current;
    const chart  = chartRef.current;
    const mpLine = mpLineRef.current;
    const mpData = mpDataRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;
    const w   = canvas.offsetWidth;
    const h   = canvas.offsetHeight;
    if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
      canvas.width  = w * dpr;
      canvas.height = h * dpr;
    }

    const ctx = canvas.getContext('2d');
    ctx.save();
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, h);

    // ── Pane title labels ──────────────────────────────────────────────
    const ph    = paneHeights(h);
    const ohlcH = h - ph.vol - ph.ema - ph.rsi;

    const labelColor = isDark ? 'rgba(156,163,175,0.75)' : 'rgba(100,116,139,0.75)';
    ctx.font         = '600 10px ui-sans-serif, system-ui, sans-serif';
    ctx.fillStyle    = labelColor;
    ctx.textBaseline = 'top';
    ctx.letterSpacing = '0.05em';

    const labels = [
      { text: 'OHLC',       y: 0 },
      ...(!isIndex ? [{ text: 'VOLUME', y: ohlcH }] : []),
      { text: 'EMA 9 / 30', y: ohlcH + ph.vol },
      { text: 'RSI 14',     y: ohlcH + ph.vol + ph.ema },
    ];
    labels.forEach(({ text, y }) => ctx.fillText(text, 10, y + 8));

    // ── Max pain bubbles (only when showMaxPain is on) ────────────────
    if (!showMaxPainRef.current || !chart || !mpLine || !mpData.length) { ctx.restore(); return; }

    const pains    = mpData.map((d) => d.total_pain ?? 0);
    const logs     = pains.map((p) => Math.log(Math.max(p, 1)));
    const logMin   = Math.min(...logs);
    const logMax   = Math.max(...logs);
    const logRange = logMax - logMin;
    const MIN_R = 8, MAX_R = 40;

    mpData.forEach((d, i) => {
      const x = chart.timeScale().timeToCoordinate(d.date);
      const y = mpLine.priceToCoordinate(d.max_pain);
      if (x == null || y == null) return;

      const r = logRange === 0
        ? (MIN_R + MAX_R) / 2
        : MIN_R + ((logs[i] - logMin) / logRange) * (MAX_R - MIN_R);

      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(251, 191, 36, 0.40)';
      ctx.fill();
    });

    ctx.restore();
  }

  // ── Create chart + all panes on mount ─────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;

    const totalH = containerRef.current.clientHeight;
    const totalW = containerRef.current.clientWidth;
    const ph = paneHeights(totalH);

    const chart = createChart(containerRef.current, {
      ...getLWCTheme(isDark),
      width:  totalW,
      height: totalH,
      timeScale: {
        ...getLWCTheme(isDark).timeScale,
        timeVisible:    true,
        secondsVisible: false,
      },
      rightPriceScale: {
        scaleMargins: { top: 0.05, bottom: 0.05 },
      },
    });
    chartRef.current = chart;

    // ── Pane 0 (auto/main): Candlestick ────────────────────────────────
    candleRef.current = chart.addSeries(CandlestickSeries, {
      upColor:         '#26a69a',
      downColor:       '#ef5350',
      borderUpColor:   '#26a69a',
      borderDownColor: '#ef5350',
      wickUpColor:     '#26a69a',
      wickDownColor:   '#ef5350',
    });

    // Max pain dashed line (same main pane)
    mpLineRef.current = chart.addSeries(LineSeries, {
      color:                  'rgba(251, 191, 36, 0.7)',
      lineWidth:              1,
      lineStyle:              2,
      priceLineVisible:       false,
      lastValueVisible:       false,
      crosshairMarkerVisible: false,
    });

    // ── Pane 1: Volume (stocks only) ───────────────────────────────────
    if (!isIndex) {
      const volPane = chart.addPane({ height: ph.vol });
      volPaneRef.current = volPane;
      volRef.current = volPane.addSeries(HistogramSeries, {
        priceFormat:  { type: 'volume' },
        priceScaleId: '',
      });
      volRef.current.priceScale().applyOptions({
        scaleMargins: { top: 0.1, bottom: 0 },
      });
    }

    // ── Pane 2: EMA ────────────────────────────────────────────────────
    const emaPane = chart.addPane({ height: ph.ema });
    emaPaneRef.current = emaPane;

    ema9Ref.current = emaPane.addSeries(LineSeries, {
      color:            '#3b82f6',
      lineWidth:        1,
      priceLineVisible: false,
      lastValueVisible: true,
      title:            'EMA 9',
    });

    ema30Ref.current = emaPane.addSeries(LineSeries, {
      color:            '#f59e0b',
      lineWidth:        1,
      priceLineVisible: false,
      lastValueVisible: true,
      title:            'EMA 30',
    });

    // ── Pane 3: RSI ────────────────────────────────────────────────────
    const rsiPane = chart.addPane({ height: ph.rsi });
    rsiPaneRef.current = rsiPane;

    const rsi = rsiPane.addSeries(LineSeries, {
      color:            '#a78bfa',
      lineWidth:        1,
      priceLineVisible: false,
      lastValueVisible: true,
      title:            'RSI 14',
      autoscaleInfoProvider: () => ({
        priceRange: { minValue: 0, maxValue: 100 },
      }),
    });
    rsiRef.current = rsi;

    rsi.createPriceLine({ price: 70, color: 'rgba(239, 68, 68, 0.6)', lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: '70' });
    rsi.createPriceLine({ price: 30, color: 'rgba(34, 197, 94, 0.6)',  lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: '30' });

    // ── Redraw bubbles on pan / zoom ───────────────────────────────────
    chart.timeScale().subscribeVisibleLogicalRangeChange(() => drawOverlay());

    // ── Tooltip via crosshair ──────────────────────────────────────────
    chart.subscribeCrosshairMove((param) => {
      const tooltip   = tooltipRef.current;
      const container = containerRef.current;
      if (!tooltip || !container) return;

      if (!param.time || !param.point || param.point.x < 0 || param.point.y < 0) {
        tooltip.style.display = 'none';
        return;
      }

      const row = dataRef.current?.find((d) => d.date === param.time);
      if (!row?.max_pain) { tooltip.style.display = 'none'; return; }

      const amount = fmtCr(row.total_pain);
      tooltip.innerHTML = `
        <div style="font-size:10px;color:#9ca3af;margin-bottom:5px">${param.time}</div>
        <div style="font-size:11px;color:#9ca3af;margin-bottom:2px">Max Pain</div>
        <div style="font-size:13px;font-weight:700;color:#fbbf24">${fmt(row.max_pain)}</div>
        ${amount ? `<div style="font-size:10px;color:#a78059;margin-top:2px">${amount}</div>` : ''}
      `;
      tooltip.style.display = 'block';

      const tipW  = tooltip.offsetWidth;
      const tipH  = tooltip.offsetHeight;
      const contW = container.offsetWidth;
      let left = param.point.x + 14;
      if (left + tipW > contW - 8) left = param.point.x - tipW - 14;
      let top = param.point.y - tipH / 2;
      if (top < 4) top = 4;
      tooltip.style.left = `${left}px`;
      tooltip.style.top  = `${top}px`;
    });

    // ── Resize observer ────────────────────────────────────────────────
    const ro = new ResizeObserver(() => {
      if (!containerRef.current || !chartRef.current) return;
      const newH = containerRef.current.clientHeight;
      const newW = containerRef.current.clientWidth;
      chartRef.current.applyOptions({ width: newW, height: newH });

      const nph = paneHeights(newH);
      try { volPaneRef.current?.applyOptions({ height: nph.vol }); } catch (_) {}
      try { emaPaneRef.current?.applyOptions({ height: nph.ema }); } catch (_) {}
      try { rsiPaneRef.current?.applyOptions({ height: nph.rsi }); } catch (_) {}

      drawOverlay(); // reposition after resize
    });
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current  = null;
      candleRef.current = null;
      volRef.current    = null;
      mpLineRef.current = null;
      ema9Ref.current   = null;
      ema30Ref.current  = null;
      rsiRef.current    = null;
      volPaneRef.current  = null;
      emaPaneRef.current  = null;
      rsiPaneRef.current  = null;
    };
  }, [isIndex]);

  // Keep stable refs current
  useEffect(() => { dataRef.current = data; }, [data]);

  // Max pain toggle — update ref, hide/show the dashed line, redraw canvas
  useEffect(() => {
    showMaxPainRef.current = showMaxPain;
    if (mpLineRef.current) {
      // Show/hide by setting series visibility option
      try {
        mpLineRef.current.applyOptions({ visible: showMaxPain });
      } catch (_) {
        // Fallback: clear data when hiding
        if (!showMaxPain) mpLineRef.current.setData([]);
        else if (mpDataRef.current.length)
          mpLineRef.current.setData(mpDataRef.current.map((d) => ({ time: d.date, value: d.max_pain })));
      }
    }
    drawOverlay();
  }, [showMaxPain]);

  // S/R toggle or new pivots — update refs and rebuild price lines
  useEffect(() => {
    showSRRef.current = showSR;
    pivotsRef.current = pivots;
    applySRLines();
  }, [showSR, pivots]);

  // ── Theme updates ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!chartRef.current) return;
    chartRef.current.applyOptions(getLWCTheme(isDark));
  }, [isDark]);

  // ── Data updates ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!candleRef.current || !data?.length) return;

    // Candlestick
    const candleData = data
      .filter((d) => d.open != null && d.high != null && d.low != null && d.close != null)
      .map((d) => ({ time: d.date, open: d.open, high: d.high, low: d.low, close: d.close }));
    candleRef.current.setData(candleData);

    // Volume
    if (volRef.current) {
      const volData = data
        .filter((d) => d.volume != null)
        .map((d) => ({
          time:  d.date,
          value: d.volume,
          color: d.close >= (d.open ?? d.close)
            ? 'rgba(38,166,154,0.5)'
            : 'rgba(239,83,80,0.5)',
        }));
      volRef.current.setData(volData);
    }

    // Max pain dashed line
    const mpData = data.filter((d) => d.max_pain != null);
    mpDataRef.current = mpData;
    if (mpLineRef.current && mpData.length > 0) {
      mpLineRef.current.setData(mpData.map((d) => ({ time: d.date, value: d.max_pain })));
    }

    // EMA 9 + EMA 30
    const aligned = data
      .filter((d) => d.close != null)
      .map((d) => ({ time: d.date, close: d.close }));
    if (ema9Ref.current)  ema9Ref.current.setData(computeEMA(aligned, 9));
    if (ema30Ref.current) ema30Ref.current.setData(computeEMA(aligned, 30));

    // RSI 14
    if (rsiRef.current) rsiRef.current.setData(computeRSI(aligned));

    chartRef.current?.timeScale().fitContent();

    // Rebuild S/R lines now that the candlestick series exists
    applySRLines();

    // Draw canvas overlay after fitContent — rAF lets LWC finish its render first
    requestAnimationFrame(() => drawOverlay());
  }, [data]);

  return (
    <div className="relative w-full h-full">
      <div ref={containerRef} className="w-full h-full" />

      {/* Canvas overlay for max pain bubbles */}
      <canvas
        ref={canvasRef}
        style={{
          position:      'absolute',
          top:           0,
          left:          0,
          width:         '100%',
          height:        '100%',
          pointerEvents: 'none',
          zIndex:        5,
        }}
      />

      {/* Hover tooltip */}
      <div
        ref={tooltipRef}
        style={{
          display:        'none',
          position:       'absolute',
          zIndex:         10,
          pointerEvents:  'none',
          userSelect:     'none',
          borderRadius:   '8px',
          padding:        '8px 12px',
          minWidth:       '130px',
          background:     isDark ? 'rgba(15,15,20,0.92)' : 'rgba(255,255,255,0.95)',
          border:         isDark ? '1px solid rgba(255,255,255,0.10)' : '1px solid rgba(0,0,0,0.10)',
          backdropFilter: 'blur(6px)',
          boxShadow:      '0 4px 16px rgba(0,0,0,0.25)',
          fontFamily:     'monospace',
        }}
      />

      {isIndex && (
        <div className="absolute bottom-2 left-3 text-[10px] text-neutral-400 dark:text-neutral-500 pointer-events-none select-none">
          Volume data unavailable for indices
        </div>
      )}
    </div>
  );
}
